import { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Translate } from 'react-redux-i18n'
import { Link } from 'react-router-dom'
import { removeAccents } from '../../../helpers/common'
import { COMPONENTS } from '../../../routers/constants'
import { Z_INDEX } from '../../../utils/common'
import { Element } from '../../html/element'
import { Notification } from '../../notification'
import { Panel } from '../../panel'
import { ScrollComponent } from '../../scrollComponent'
import { TextEllipsisStaticLine } from '../../textEllipsis/TextEllipsisStaticLine'
import { PopupAction } from '../PopupAction'
import { MAX_COMPANY_IN_PORTFOLIO } from './constant'
import classes from './index.module.scss'
import {
  selectLoading,
  selectPortfolios,
  selectSearchTerm,
} from './store/slice'
import { addNewCompany, deleteCompany } from './store/thunk'

export const PortfolioList = ({ organizationId }) => {
  const portfolios = useSelector(selectPortfolios)
  const searchTerm = useSelector(selectSearchTerm)

  const portfolioList = useMemo(() => {
    return portfolios.filter((item) =>
      removeAccents(item.portfolioName.toLowerCase()).includes(
        removeAccents(searchTerm.toLowerCase()),
      ),
    )
  }, [portfolios, searchTerm])

  return (
    <div className={classes.portfolioList}>
      <Panel
        label="common.popup.manageCompanyPortfolio.PORTFOLIO_NAME"
        isI18n
        height={400}
      >
        {!portfolioList.length ? (
          <Notification />
        ) : (
          <ScrollComponent>
            {portfolioList.map((item, index) => (
              <PortfolioItem
                key={item.portfolioId}
                organizationId={organizationId}
                item={item}
                index={index}
              />
            ))}
          </ScrollComponent>
        )}
      </Panel>
    </div>
  )
}

const PortfolioItem = ({ organizationId, item, index }) => {
  return (
    <Element
      className={classes.portfolioItem}
      style={{ backgroundColor: index % 2 ? '#F9F9F9' : 'transparent' }}
    >
      <Element style={{ color: '#2C73E8' }}>
        <Link
          to={`${COMPONENTS.library.components.savedPortfolios.components.detailsPortfolio.route}?id=${item.portfolioId}`}
          target="_blank"
        >
          <TextEllipsisStaticLine
            text={item.portfolioName}
            isI18n={false}
            lineClamp={1}
            zIndexTooltip={Z_INDEX.MODAL + 1}
          />
        </Link>
      </Element>

      <div>
        {item.hasOrganizationId ? (
          <RemovePortfolio organizationId={organizationId} item={item} />
        ) : (
          <AddPortfolio organizationId={organizationId} item={item} />
        )}
      </div>
    </Element>
  )
}

const AddPortfolio = ({ organizationId, item }) => {
  const dispatch = useDispatch()
  const isLoading = useSelector(selectLoading('mutateData'))

  const [isShow, setIsShow] = useState(false)

  const isMaxCompany = item.numOfCompanies >= MAX_COMPANY_IN_PORTFOLIO

  const handleShow = () => {
    if (isMaxCompany) return
    setIsShow(true)
  }

  const handleClose = () => {
    setIsShow(false)
  }

  const handleAdd = () => {
    if (isLoading) return

    dispatch(
      addNewCompany({
        organizationIds: [organizationId],
        portfolioIds: [item.portfolioId],
      }),
    )
      .unwrap()
      .then(() => {
        handleClose()
      })
  }

  return (
    <>
      <Element
        className={classes.button}
        style={{
          opacity: isMaxCompany ? 0.4 : 1,
          cursor: isMaxCompany ? 'not-allowed' : 'pointer',
          fontSize: 10,
          borderColor: '#2C73E8',
          backgroundColor: '#2C73E8',
          color: '#FFFFFF',
        }}
        onClick={handleShow}
      >
        <Translate value="common.popup.manageCompanyPortfolio.ADD" />
      </Element>
      <PopupAction
        isShow={isShow}
        title={'common.CONFIRMATION'}
        handleClose={handleClose}
        btnSubmit={{
          label: 'common.YES',
          isI18n: true,
          event: handleAdd,
          disabled: isLoading,
        }}
        btnCancel={{
          label: 'common.NO',
          isI18n: true,
          event: handleClose,
        }}
      >
        <Translate value="common.popup.manageCompanyPortfolio.ADD_TO_PORT" />
      </PopupAction>
    </>
  )
}

const RemovePortfolio = ({ organizationId, item }) => {
  const dispatch = useDispatch()
  const isLoading = useSelector(selectLoading('mutateData'))

  const [isShow, setIsShow] = useState(false)

  const handleShow = () => {
    setIsShow(true)
  }

  const handleClose = () => {
    setIsShow(false)
  }

  const handleRemove = () => {
    if (isLoading) return

    dispatch(
      deleteCompany({
        organizationIds: [organizationId],
        portfolioIds: [item.portfolioId],
        deleteType: 'IN',
      }),
    )
      .unwrap()
      .then(() => {
        handleClose()
      })
  }

  return (
    <>
      <Element
        className={classes.button}
        style={{ color: '#2C73E8', fontSize: 10, borderColor: '#6D9DCF' }}
        onClick={handleShow}
      >
        <Translate value="common.popup.manageCompanyPortfolio.REMOVE" />
      </Element>

      <PopupAction
        isShow={isShow}
        title={'common.CONFIRMATION'}
        handleClose={handleClose}
        btnSubmit={{
          label: 'common.YES',
          isI18n: true,
          event: handleRemove,
          disabled: isLoading,
        }}
        btnCancel={{
          label: 'common.NO',
          isI18n: true,
          event: handleClose,
        }}
      >
        <Translate value="common.popup.manageCompanyPortfolio.REMOVE_FROM_PORT" />
      </PopupAction>
    </>
  )
}
